import { t } from "@hakili/i18n";
import { ArrowLeft, Phone } from "lucide-react";

/**
 * `round CW`, `CW.3`: **« Voir le véhicule », `WF-CLI-07`.**
 *
 * The seller's card with the road distance, the two ways to reach him, and the
 * intent card that opens the purchase.
 *
 * ## Both controls record the SAME fact
 *
 * `P1-CAT-010`: « Demander une visite » and the `tel:` link write one
 * `catalog.visit_requests` row, distinguished by `source` , and the door
 * answers the FIRST record inside 24 hours, so a buyer who telephones and then
 * presses has asked once. **The `tel:` link records BEFORE it dials**, because
 * a browser leaving for the dialler is a page that may never come back.
 *
 * ## The shortcut and the intent card are never shown together
 *
 * `WF-CLI-07` step 3 says so in terms: the eligibility shortcut disappears the
 * moment the intent card is displayed, *"to avoid two identical controls"*.
 */

export interface VisitProps {
  readonly title: string;
  readonly sellerName: string | null;
  readonly city: string | null;
  readonly district: string | null;
  readonly distanceKm: number | null;
  readonly travelTime: string | null;
  readonly phone: string | null;
  /** A live dossier on this car: the in-progress card replaces both controls. */
  readonly dossierEnCours: boolean;
  readonly intentOpen: boolean;
  /** `T-2b`: the door's refusal on « Je paie comptant », in its own words , a gate may close, never in silence. */
  readonly intentRefusal?: string | null;
  readonly onBack: () => void;
  readonly onAskVisit: () => void;
  readonly onCall: () => void;
  readonly onOpenIntent: () => void;
  readonly onCheckEligibility: () => void;
  readonly onFinance: () => void;
  readonly onCash: () => void;
}

export function Visit(props: VisitProps): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <button
          type="button"
          data-testid="visite-retour"
          aria-label={t("common.action.retour")}
          onClick={props.onBack}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white"
        >
          <ArrowLeft className="h-4 w-4 text-gray-600" aria-hidden="true" />
        </button>
        <h1 className="text-xl font-semibold tracking-tight text-gray-900">{t("client.visite.titre")}</h1>
      </div>

      <section
        data-testid="visite-vendeur"
        className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5"
      >
        <p className="text-sm font-semibold text-gray-900">{props.title}</p>
        {/* The seller's NAME is `identity`'s and `catalog` cannot reach it
            without a module cycle (`CW.3`), so a dash rather than an invented
            name , `R81`. */}
        <p className="mt-0.5 text-sm text-gray-500">{props.sellerName ?? "-"}</p>
        <p className="mt-1 text-xs tabular-nums text-gray-500">
          {[props.city, props.district].filter((part) => part !== null && part !== "").join(", ")}
          {props.distanceKm === null
            ? ""
            : ` · ${t("client.carte.a")} ${String(props.distanceKm)} ${t("client.carte.km")}${
                props.travelTime === null ? "" : `, ${props.travelTime}`
              }`}
        </p>
      </section>

      {props.dossierEnCours ? (
        <section
          data-testid="visite-dossier-en-cours"
          className="rounded-2xl border border-indigo-200 bg-indigo-50 px-4 py-3.5"
        >
          <p className="text-sm font-medium text-indigo-800">{t("client.visite.dossierEnCours")}</p>
        </section>
      ) : (
        <>
          <button
            type="button"
            data-testid="visite-demander"
            onClick={props.onAskVisit}
            className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white"
          >
            {t("client.visite.demander")}
          </button>

          {props.phone === null ? (
            /**
             * `R81` as amended: a control before its door keeps the drawing's
             * look, GREYED, with its reason in its own title.
             *
             * `identity.organisations` holds no telephone , name, type, status,
             * the opening facts and the verification flags, and nothing more ,
             * so the seller port cannot answer one and this cannot dial. The
             * field is filed for the identity round; the door behind the
             * control already takes `source: "appel"` and is tested.
             *
             * Deleting it would redraw the screen; making it live would offer a
             * number nobody has.
             */
            <button
              type="button"
              data-testid="visite-appeler"
              disabled
              title={t("client.visite.appelerSansPorte")}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-3.5 text-sm font-medium text-gray-700 opacity-50"
            >
              <Phone className="h-4 w-4 text-gray-400" aria-hidden="true" />
              {t("client.visite.appeler")} -
            </button>
          ) : (
            <a
              data-testid="visite-appeler"
              href={`tel:${props.phone}`}
              onClick={() => {
                // **Recorded BEFORE the dial.** The browser is about to leave
                // for the dialler and may never come back; a record written
                // afterwards would be a record that depends on the buyer
                // returning.
                props.onCall();
              }}
              className="flex w-full items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white py-3.5 text-sm font-medium text-gray-700"
            >
              <Phone className="h-4 w-4 text-gray-400" aria-hidden="true" />
              {t("client.visite.appeler")} {props.phone}
            </a>
          )}

          {/* `WF-CLI-07` step 3: the shortcut goes the moment the intent card
              appears , two controls for one gesture is the thing it forbids. */}
          {props.intentOpen ? (
            <section
              data-testid="visite-intention"
              className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5"
            >
              <p className="text-sm font-semibold text-gray-900">{t("client.visite.commentPayer")}</p>
              {props.intentRefusal === null || props.intentRefusal === undefined ? null : (
                <p role="alert" data-testid="visite-intention-refus" className="mt-2 text-sm text-rose-600">
                  {props.intentRefusal}
                </p>
              )}
              <div className="mt-3 flex flex-col gap-2">
                <button
                  type="button"
                  data-testid="visite-financer"
                  onClick={props.onFinance}
                  className="w-full rounded-2xl bg-indigo-600 py-3 text-sm font-medium text-white"
                >
                  {t("client.visite.financer")}
                </button>
                <button
                  type="button"
                  data-testid="visite-comptant"
                  onClick={props.onCash}
                  className="w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700"
                >
                  {t("client.visite.comptant")}
                </button>
              </div>
            </section>
          ) : (
            <>
              <button
                type="button"
                data-testid="visite-acheter"
                onClick={props.onOpenIntent}
                className="w-full rounded-2xl border border-indigo-200 bg-white py-3.5 text-sm font-medium text-indigo-600"
              >
                {t("client.visite.acheter")}
              </button>
              <button
                type="button"
                data-testid="visite-eligibilite"
                onClick={props.onCheckEligibility}
                className="w-full rounded-2xl border border-gray-200 bg-white py-3 text-sm font-medium text-gray-700"
              >
                {t("client.visite.raccourci")}
              </button>
            </>
          )}
        </>
      )}
    </div>
  );
}
